import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Database, Warning, CheckCircle, ArrowLeft } from "@phosphor-icons/react";
import { formatApiErrorDetail } from "@/lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex h-screen w-screen items-center justify-center bg-white" data-testid="forgot-password-page">
      <div className="relative z-10 w-full max-w-sm rounded-sm border border-zinc-300 bg-white p-8">
        <div className="mb-6 flex flex-col items-center gap-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-sm border border-zinc-300 bg-zinc-50">
            <Database size={20} weight="bold" className="text-blue-600" />
          </div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-zinc-950">Forgot Password</h1>
          <p className="text-center text-xs text-zinc-500">
            Enter your account email and we'll send you a password reset link.
          </p>
        </div>

        {sent ? (
          <div className="flex flex-col gap-4">
            <div
              className="flex items-center gap-2 rounded-sm border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-700"
              data-testid="forgot-password-success-message"
            >
              <CheckCircle size={16} />
              If an account with that email exists, a reset link has been sent. Please check your inbox.
            </div>
            <Link to="/login" className="flex items-center justify-center gap-1.5 text-sm text-blue-600 hover:underline" data-testid="back-to-login-link">
              <ArrowLeft size={14} /> Back to Sign In
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-4" data-testid="forgot-password-form">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="email" className="text-xs uppercase tracking-[0.05em] text-zinc-500">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="rounded-sm border-zinc-300"
                data-testid="forgot-password-email-input"
              />
            </div>
            {error && (
              <div className="flex items-center gap-2 rounded-sm border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="forgot-password-error-message">
                <Warning size={16} /> {error}
              </div>
            )}
            <Button type="submit" disabled={loading} className="mt-2 rounded-sm bg-blue-600 text-white hover:bg-blue-700" data-testid="forgot-password-submit-button">
              {loading ? "Sending..." : "Send Reset Link"}
            </Button>
            <Link to="/login" className="flex items-center justify-center gap-1.5 text-sm text-zinc-500 hover:text-blue-600" data-testid="back-to-login-link">
              <ArrowLeft size={14} /> Back to Sign In
            </Link>
          </form>
        )}
      </div>
    </div>
  );
}
